var AccelerateSupport = require("enyo-ui/AccelerateSupport"),
    Control           = require("enyo/Control"),
    dom               = require("enyo/dom"),
    frameThrottle     = require("enyo-perf/utils").frameThrottle,
    kind              = require("enyo/kind"),
    utils             = require("enyo/utils");

require("enyo-ui");
require("./ScrollThumb.css");

/**
 * [ScrollThumb]{@link enyo-ui.ScrollThumb} is a control that serves as a visual
 * indicator of the position and size of a scrollers' visible content. It does
 * this by positioning and scaling itself relative to the bounds of a `track`
 * component (such as the track of a scroll-bar).
 *
 * @ui
 * @class   enyo-ui.ScrollThumb
 * @extends enyo.Control
 * @mixes   enyo-ui.AccelerateSupport
 * @public
 */
module.exports = kind(/** @lends enyo-ui.ScrollThumb.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.ScrollThumb",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AccelerateSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-scroll-thumb",

    /**
     * Whether to gpu-accelerate this controls' DOM Node.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    accelerate: true,

    /**
     * The ResizeObserver instance that observes the size of the scroll track.
     *
     * @type {?ResizeObserver}
     * @private
     */
    resizeObserver: null,

    /**
     * Cached size of the track.
     *
     * @type {?Object}
     * @private
     */
    trackSize: {
        width:  0,
        height: 0
    },

    /**
     * The horizontal offset of the thumb relative to it's container.
     *
     * @type {?number}
     * @readonly
     * @private
     */
    positionX: null,

    /**
     * The vertical offset of the thumb relative to it's container.
     *
     * @type {?number}
     * @readonly
     * @private
     */
    positionY: null,

    /**
     * The width of the thumb in px, representing the relative size of the
     * visible content in the scroller.
     *
     * @type {?number}
     * @readonly
     * @private
     */
    width: null,

    /**
     * The height of the thumb in px, representing the relative size of the
     * visible content in the scroller.
     *
     * @type {?number}
     * @readonly
     * @private
     */
    height: null,

    /**
     * Whether to use transforms for positioning of the thumb.
     *
     * @type    {boolean}
     * @default true
     * @private
     */
    transform: true,

    /**
     * The scroller.
     *
     * @type    {?enyo-ui.Scroller}
     * @default null
     * @public
     */
    scroller: null,

    /**
     * The track control.
     *
     * @type    {?enyo.Control}
     * @default null
     * @public
     */
    track: null,

    /**
     * Sets which axis the thumb tracks. Supported values are "vertical",
     * "horizontal", "both" or `null`.
     *
     * @type    {?string}
     * @default null
     * @public
     */
    axis: null,

    /**
     * Set a minimum width for the thumb.
     *
     * @type    {number}
     * @default 10
     * @public
     */
    minWidth: 10,

    /**
     * Set a minimum height for the thumb.
     *
     * @type    {number}
     * @default 10
     * @public
     */
    minHeight: 10,

    /**
     * @private
     */
    observers: {
        sync: ["axis", "minWidth", "minHeight"]
    },

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.onNodeResized  = frameThrottle(this.bindSafely(this.onNodeResized));
            this.trackSize      = utils.clone(this.trackSize);
            sup.apply(this, arguments);
            this.transform      = dom.canTransform();
            this.resizeObserver = new ResizeObserver(this.onNodeResized);
        };
    }),

    /**
     * Set a css class to match the scroll axis.
     *
     * @param {string}  oldAxis  The old axis
     * @param {string}  newAxis  The new axis
     * @private
     */
    axisChanged: function(oldAxis, newAxis) {
        switch (oldAxis) {
            case "vertical":
            case "horizontal":
            case "both":
                this.removeClass("enyo-ui-scroll-thumb--scroll-axis-" + oldAxis);
                break;
            case null:
                this.removeClass("enyo-ui-scroll-thumb--scroll-axis-none");
                break;
            default:
                break;
        }

        switch (newAxis) {
            case "vertical":
            case "horizontal":
            case "both":
                this.addClass("enyo-ui-scroll-thumb--scroll-axis-" + newAxis);
                break;
            case null:
                this.addClass("enyo-ui-scroll-thumb--scroll-axis-none");
                break;
            default:
                this.error("Invalid scroll axis", newAxis);
                break;
        }
    },

    // ...........................
    // Layout

    /**
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            this.resizeObserver.observe(this.hasNode());
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    teardownRender: kind.inherit(function(sup) {
        return function() {
            this.resizeObserver.unobserve(this.hasNode());
            sup.apply(this, arguments);
        };
    }),

    /**
     * Respond to resize events.
     *
     * @private
     */
    onNodeResized: function() {
        this.reflow();
    },

    /**
     * Thumb should update on reflow.
     *
     * @private
     */
    reflow: function() {
        var track       = this.track,
            trackBounds = track && track.hasNode() ? track.getBounds() : {
                width:  0,
                height: 0
            };

        if (this.trackSize.width !== trackBounds.width
            || this.trackSize.height !== trackBounds.height) {
            this.trackSize = {
                width:  trackBounds.width,
                height: trackBounds.height
            };
            this.sync();
        }
    },

    /**
     * Scales and positions the thumb based on the scrollers' state and the axis
     * monitored by this thumb:
     *
     *  - If [axis]{@link enyo-ui.ScrollThumb#axis} is set to `horizontal`,
     *    thumb is scaled and positioned horizontally while vertical size and
     *    position styles are cleared.
     *  - If [axis]{@link enyo-ui.ScrollThumb#axis} is set to `vertical`, thumb
     *    is scaled and positioned vertically while horizontal size and position
     *    styles are cleared.
     *  - If [axis]{@link enyo-ui.ScrollThumb#axis} is set to `both`, thumb is
     *    scaled and positioned both horizontally and vertically.
     *
     * @public
     */
    sync: function() {
        var scroller      = this.scroller,
            trackSize     = this.trackSize,
            axis          = this.axis,
            accelerate    = this.accelerate,
            scrollerDimensions,
            scrollPosition,
            scrollerWidth,
            scrollerHeight,
            contentWidth,
            contentHeight,
            overscrollX,
            overscrollY,
            trackWidth,
            trackHeight,
            scaleX        = 1,
            scaleY        = 1,
            multiplierX   = 1,
            multiplierY   = 1,
            width         = null,
            height        = null,
            positionX     = null,
            positionY     = null,
            translate     = {},
            bounds        = {},
            boundsChanged = false;

        if (!scroller || !trackSize) {
            return;
        }

        scrollerDimensions = scroller.dimensions;
        scrollPosition     = scroller.scrollPosition;

        // Horizontal scroll state
        if (axis === "horizontal" || axis === "both") {
            scrollerWidth = scrollerDimensions.scrollerWidth;
            contentWidth  = scrollerDimensions.contentWidth;
            overscrollX   = scrollPosition.overscrollX;
            trackWidth    = trackSize.width;

            // Size of visible content width relative to full content width.
            if (contentWidth > 0 && scrollerWidth > 0) {
                scaleX = Math.min(1, 1 / (contentWidth / scrollerWidth));
            }

            // Size of track width relative to scroller width.
            if (scrollerWidth > 0 && trackWidth > 0) {
                multiplierX = 1 / (scrollerWidth / trackWidth);
            }

            // Calculate thumb width
            width = Math.max(this.minWidth, (trackWidth - Math.abs(overscrollX * multiplierX)) * scaleX);

            // Calculate positionX
            if (overscrollX !== 0) {
                positionX = overscrollX > 0 ? trackWidth - width : 0;
            }
            else {
                positionX = scrollPosition.scrollLeft * scaleX * multiplierX;
            }
        }

        // Vertical scroll state
        if (axis === "vertical" || axis === "both") {
            scrollerHeight = scrollerDimensions.scrollerHeight;
            contentHeight  = scrollerDimensions.contentHeight;
            overscrollY    = scrollPosition.overscrollY;
            trackHeight    = trackSize.height;

            // Size of visible content height relative to full content height.
            if (contentHeight > 0 && scrollerHeight > 0) {
                scaleY = Math.min(1, 1 / (contentHeight / scrollerHeight));
            }

            // Size of track height relative to scroller height.
            if (scrollerHeight > 0 && trackHeight > 0) {
                multiplierY = 1 / (scrollerHeight / trackHeight);
            }

            // Calculate thumb height
            height = Math.max(this.minHeight, (trackHeight - Math.abs(overscrollY * multiplierY)) * scaleY);

            // Calculate positionY
            if (overscrollY !== 0) {
                positionY = overscrollY > 0 ? trackHeight - height : 0;
            }
            else {
                positionY = scrollPosition.scrollTop * scaleY * multiplierY;
            }
        }

        // Update thumb size as needed
        if (width !== this.width || height !== this.height) {
            bounds.width  = width !== null ? width + "px" : null;
            bounds.height = height !== null ? height + "px" : null;
            boundsChanged = true;
        }

        // Update transform as needed
        if (positionX !== this.positionX || positionY !== this.positionY) {
            if (this.transform) {
                if (accelerate) {
                    translate.translateZ = "0";
                }

                if (positionX !== null) {
                    translate.translateX = positionX + "px";
                }

                if (positionY !== null) {
                    translate.translateY = positionY + "px";
                }
                dom.transform(this, translate);
            }
            else {
                bounds.left   = positionX !== null ? positionX + "px" : null;
                bounds.top    = positionY !== null ? positionY + "px" : null;
                boundsChanged = true;
            }
        }

        // Apply bounds as needed
        if (boundsChanged) {
            this.setBounds(bounds);
        }

        // Memorize
        this.positionX = positionX;
        this.positionY = positionY;
        this.width     = width;
        this.height    = height;
    },

    // ...........................
    // Destruction

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.resizeObserver.disconnect();
            sup.apply(this, arguments);
        };
    })
});
